// @file Surface attachments store
import { getDisplayAttributes, memoizedArvoFetchLink } from '@@/bits/beethoven'
import { captureMessage } from '@@/bits/error_tracker'
import { vSet } from '@@/bits/vue'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Cid } from '@@/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { type Link } from '@padlet/arvo'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { isEmpty } from 'lodash-es'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useSurfaceAttachmentsStore = defineStore('surfaceAttachments', () => {
  const surfacePostsStore = useSurfacePostsStore()

  /* ---------------------- */
  /* STATE                  */
  /* ---------------------- */

  /**
   * A map of `Link` object by its URL.
   * The `Link` object is the serialization of the link record in the `links` table.
   */
  const linkAttributesByUrl = ref<Record<string, Link>>({})
  /**
   * A map of `BeethovenDisplayAttributes` by its URL.
   * This `BeethovenDisplayAttributes` object is a transformation of the `Link` object
   * through the `getDisplayAttributes` function from @padlet/beethoven-client.
   * However, we shouldn't store this data in Pinia because display attributes are
   * dependent on the post size. It should be up to the component to get the display
   * attributes suitable for its use case.
   * @deprecated Should migrate to `linkAttributesByUrl`.
   */
  const linkDisplayAttributes = ref<Record<string, BeethovenDisplayAttributes | null>>({})
  const surfacePreviewImageLoaded = ref<Record<string, boolean>>({})
  const fileUploadingToPickerBox = ref<File | null>(null)

  /* ---------------------- */
  /* GETTER FUNCTIONS       */
  /* ---------------------- */

  const getLinkAttributesForUrl = (url: string): Link | null => {
    return linkAttributesByUrl.value[url] ?? null
  }

  const getLinkDisplayAttributesForUrl = (url: string): BeethovenDisplayAttributes | null => {
    return linkDisplayAttributes.value[url] ?? null
  }

  const getLinkDisplayAttributesForPost = (postCid: string): BeethovenDisplayAttributes | null => {
    const post = surfacePostsStore.postEntitiesByCid[postCid]
    if (post == null) return null
    const attachmentUrl = post.wish_content?.attachment_props?.url ?? post.attachment
    return !isEmpty(attachmentUrl) ? getLinkDisplayAttributesForUrl(attachmentUrl) : null
  }

  const isSurfacePreviewImageLoadedForUrl = (url: string): boolean => {
    return surfacePreviewImageLoaded[url] ?? false
  }

  /* ---------------------- */
  /* ACTIONS                */
  /* ---------------------- */

  const fetchAndStoreLinkAttributes = async (url: string): Promise<void> => {
    try {
      const link = await memoizedArvoFetchLink(url)
      const attributes = (link.data as JsonAPIResource<Link>).attributes
      vSet(linkAttributesByUrl.value, url, attributes)
    } catch (error) {
      captureMessage('Fetch link from Arvo error', {
        level: 'error',
        context: { url, error },
      })
    }
  }

  const uploadFileToPickerBox = (file: File | null): void => {
    fileUploadingToPickerBox.value = file
  }
  const completeUploadingFileToPickerBox = (): void => {
    fileUploadingToPickerBox.value = null
  }

  const updateLinkDisplayAttributes = ({
    url,
    attributes,
  }: {
    url: string
    attributes: BeethovenDisplayAttributes | null
  }): void => {
    linkDisplayAttributes.value = {
      ...linkDisplayAttributes.value,
      [url]: attributes,
    }
  }

  const cancelUploadFileToPickerBox = (): void => {
    fileUploadingToPickerBox.value = null
  }

  const setSurfacePreviewImageLoaded = ({ url }: { url?: string }): void => {
    if (url != null) {
      surfacePreviewImageLoaded.value = {
        ...surfacePreviewImageLoaded.value,
        [url]: true,
      }
    }
  }

  const fetchDisplayAttributesForPost = async (postCid: Cid): Promise<void> => {
    const post = surfacePostsStore.postEntitiesByCid[postCid]
    if (post == null) return

    const attachmentUrl = post.wish_content?.attachment_props?.url ?? post.attachment
    if (isEmpty(attachmentUrl) || linkDisplayAttributes.value[attachmentUrl] != null) return
    const displayAttributes = await getDisplayAttributes(attachmentUrl)
    updateLinkDisplayAttributes({ url: attachmentUrl, attributes: displayAttributes })
  }

  return {
    // state
    linkAttributesByUrl,
    linkDisplayAttributes,
    surfacePreviewImageLoaded,
    fileUploadingToPickerBox,

    // getter functions
    getLinkAttributesForUrl,
    getLinkDisplayAttributesForUrl,
    getLinkDisplayAttributesForPost,
    isSurfacePreviewImageLoadedForUrl,

    // actions
    fetchAndStoreLinkAttributes,
    uploadFileToPickerBox,
    cancelUploadFileToPickerBox,
    completeUploadingFileToPickerBox,
    updateLinkDisplayAttributes,
    setSurfacePreviewImageLoaded,
    fetchDisplayAttributesForPost,
  }
})
